import React, { useState, useEffect, useContext, useMemo } from "react";
import {
    Input,
    Button,
    Card,
    Row,
    Col,
    Select,
    Typography,
    message,
    Spin,
    Rate,
    Divider,
    Tag,
    Skeleton
} from "antd";
import GlobalTooltip from "./GlobalTooltip";
import { useParams, useNavigate } from "react-router-dom";
import ShopServ from "../service/ShopServ";
import ProductServ from "../service/ProductServ";
import CategoryServ from "../service/CategoryServ";
import AuthServ from "../service/AuthServ";
import { get } from "../service/base";
import Navbar from "../pages/Navbar";
import { CartContext } from "../CartContext";
import { WishlistContext } from "../WishlistContext";
import {
    IconHeart,
    IconHeartFilled,
    IconPlus,
    IconMinus,
    IconMapPin,
    IconClock,
    IconTruck,
    IconStar,
    IconFilter,
    IconMessage,
    IconShoppingCart,
    IconPackage,
    IconMoodEmpty
} from "@tabler/icons-react";
import ChatServ from "../service/ChatServ";
import "../css/shopDetails.css";
import GlobalButton from "../util/GlobalButton";
import { useGlobalAuthModal } from "../util/GlobalAuthModal";
import CheckoutModal from "./CheckoutPage";
import ChatWindow from "./ChatWindow";
import SafeImage from "./SafeImage";
import ProductCardSkeleton from "./skeletons/ProductCardSkeleton";
import { useProductSelection } from "../util/useProductSelection";
import { formatUnit, renderSafeText } from "../util/unitFormatter";
import { LocationContext } from "../LocationContext";
import { useDeliveryEstimate } from "../hooks/useDeliveryEstimate";
import ProductCard from "./ProductCard";
const { Title, Text } = Typography;

const { Option } = Select;
const { Meta } = Card;




const ShopDetailsSkeleton = () => (
    <div className="shop-details-wrapper skeleton-active">
        <div className="shop-details-layout">
            <aside className="shop-sidebar">
                <div className="sidebar-card shop-info-card">
                    <div className="shop-header-mini">
                        <Skeleton.Avatar active size={64} shape="circle" />
                        <div className="pcu-flex-1">
                            <Skeleton active paragraph={{ rows: 1 }} title={{ width: '80%' }} />
                        </div>
                    </div>
                    <Divider className="pcu-margin-y-15" />
                    <div className="shop-meta-list">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="meta-item pcu-margin-bottom-12">
                                <Skeleton.Button active size="small" className="pcu-w-20 pcu-h-20 pcu-margin-right-10" />
                                <Skeleton.Button active size="small" className="pcu-w-70 pcu-h-16" />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="sidebar-card filters-card">
                    <Skeleton active paragraph={{ rows: 6 }} />
                </div>
            </aside>

            <div className="shop-main-content">
                <div className="shop-categories-bar pcu-flex pcu-gap-12 pcu-overflow-hidden">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <Skeleton.Button key={i} active className="pcu-w-100 pcu-h-40 pcu-radius-20" />
                    ))}
                </div>

                <div className="products-grid-header pcu-margin-top-30">
                    <Skeleton.Button active className="pcu-w-250 pcu-h-32" />
                </div>

                <div className="products-grid">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                        <ProductCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const ShopDetails = () => {
    const { showAlert } = useGlobalAuthModal();
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [checkoutProduct, setCheckoutProduct] = useState(null);
    const [products, setProducts] = useState([]);
    const [originalProducts, setOriginalProducts] = useState([]);
    const [shopInfo, setShopInfo] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("All");
    // const [selectedOptions, setSelectedOptions] = useState({}); // Removed unused options state for listing
    // Filter states
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [availability, setAvailability] = useState("all"); // 'all', 'in-stock'
    const [minRating, setMinRating] = useState(0);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [allCategoriesList, setAllCategoriesList] = useState([]);
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("all");

    const [loading, setLoading] = useState(true);
    const [shopLoading, setShopLoading] = useState(true);

    // 💬 CHAT STATES
    const [chatOpen, setChatOpen] = useState(false);
    const [activeConversation, setActiveConversation] = useState(null);
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    const navigate = useNavigate();
    const { shopId } = useParams();
    const { cart, addToCart, updateQuantity, removeFromCart } = useContext(CartContext);
    const { toggleWishlist, isInWishlist } = useContext(WishlistContext);
    const { latitude, longitude } = useContext(LocationContext);
    const { formatETA } = useDeliveryEstimate(shopId, latitude, longitude);

    useEffect(() => {
        fetchData();
    }, [shopId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setShopLoading(true);

            // Fetch products, shop info, and ALL categories in parallel
            const [productsRes, shopRes, categoriesRes] = await Promise.all([
                ProductServ.getByShop(shopId),
                ShopServ.single(shopId),
                CategoryServ.index({ limit: 'all' })
            ]);

            // Handle Products
            let items = [];
            if (Array.isArray(productsRes)) items = productsRes;
            else if (productsRes && productsRes.data) items = productsRes.data;

            const normalized = items.map((p) => ({
                ...p, // Keep original fields for ProductCard
                _id: p._id || p.id, // Ensure _id is present for ProductCard
                id: p._id || p.id,
                name: p.name || p.title || p.productName,
                imageUrl: p.imageUrl || p.image || "/placeholder.png",
                images: p.images || [],
                category: p.category || p.categoryName || "Other",
                filterCategory: p.subCategoryId?.name || p.subCategory || p.subCategoryName || p.category || p.categoryName || "Other",
                price: p.price || 0,
                mrp: p.mrp || p.price,
                rating: p.rating || 0,
                stock: p.stock || 0,
                discount: p.discount || 0,
                description: p.description || "",
                options: p.options || [],
                variants: p.variants || [],
                variantType: p.variantType,
                hasVariants: p.hasVariants || (p.variants && p.variants.length > 0) || (p.options && p.options.length > 0),
                shopId: p.shopId,
            }));

            setProducts(normalized);
            setOriginalProducts(normalized);

            // Handle Categories
            const allCats = categoriesRes?.data || categoriesRes || [];
            setAllCategoriesList(allCats);

            // 1. Identify the root category for this shop
            // (Assumed from the first product's root category or shop metadata)
            let rootId = null;
            if (normalized.length > 0) {
                const firstProd = normalized[0];
                const pCatId = firstProd.subCategoryId?._id || firstProd.subCategoryId || firstProd.categoryId;

                const findRoot = (id) => {
                    const cat = allCats.find(c => (c._id || c.id) === id);
                    if (!cat || !cat.parentId) return id;
                    return findRoot(cat.parentId?._id || cat.parentId);
                };
                rootId = findRoot(pCatId);
            }

            // 2. Extract ALL subcategories of that root for the sidebar
            let initialSidebarCats = [];
            if (rootId) {
                const subCats = allCats.filter(c => (c.parentId?._id || c.parentId) === rootId);
                initialSidebarCats = subCats.map(c => ({
                    name: c.name,
                    id: c._id || c.id
                }));
            } else {
                // Fallback: unique names from products if no root found
                const uniqueSubCatNames = [...new Set(normalized.map(p => p.filterCategory).filter(Boolean))];
                initialSidebarCats = uniqueSubCatNames.map(name => {
                    const found = allCats.find(c => c.name === name);
                    return { name, id: found?._id || found?.id || name };
                });
            }
            setCategories(initialSidebarCats);

            // Handle Shop Info
            if (shopRes) {
                setShopInfo(shopRes);
            }

        } catch (err) {
            console.error("Error fetching data:", err);
            message.error("Failed to load shop details");
        } finally {
            setLoading(false);
            setShopLoading(false);
        }
    };

    // Filter Logic
    useEffect(() => {
        let filtered = [...originalProducts];

        // Category Filter
        if (selectedSubCategoryId !== "all") {
            filtered = filtered.filter(p => {
                const pSubId = p.subCategoryId?._id || p.subCategoryId || p.categoryId;
                return pSubId && (pSubId === selectedSubCategoryId || pSubId._id === selectedSubCategoryId);
            });
        }

        // Price Filter
        if (minPrice) filtered = filtered.filter(p => p.price >= Number(minPrice));
        if (maxPrice) filtered = filtered.filter(p => p.price <= Number(maxPrice));

        // Availability Filter
        if (availability === "in-stock") {
            filtered = filtered.filter(p => p.stock > 0);
        }

        // Rating Filter
        if (minRating > 0) {
            filtered = filtered.filter(p => p.rating >= minRating);
        }

        setProducts(filtered);
    }, [selectedSubCategoryId, minPrice, maxPrice, availability, minRating, originalProducts]);

    const handleCategoryClick = (category) => {
        const catId = category.id;
        const catName = category.name;
        setSelectedSubCategoryId(catId);
        setSelectedCategory(catName);
    };


    // Memoized sidebar categories based on mode
    const sidebarCategories = categories;



    const handleIncrease = async (e, productId, currentQty) => {
        e.stopPropagation();
        const cartItem = cart?.items?.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            await updateQuantity(cartItem._id, currentQty + 1);
        }
    };

    const handleDecrease = async (e, productId, currentQty) => {
        e.stopPropagation();
        const cartItem = cart?.items?.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            if (currentQty > 1) {
                await updateQuantity(cartItem._id, currentQty - 1);
            } else {
                await removeFromCart(cartItem._id);
            }
        }
    };

    const handleWishlistToggle = async (e, productId) => {
        e.stopPropagation();
        if (!AuthServ.isLogged()) {
            showAlert({
                title: "Authentication Required",
                description: "Please log in to use the wishlist.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    { text: "Login", variant: "primary", onClick: () => navigate("/customer-login") }
                ]
            });
            return;
        }
        await toggleWishlist(productId);
    };

    // ------------------- ADD/UPDATE CART -------------------
    const handleAddToCart = async (e, product) => {
        e.stopPropagation();
        
        await addToCart(product.id || product._id, 1, {}, {
            name: product.name,
            image: product.imageUrl || (product.images && product.images[0]),
            price: product.price,
            mrp: product.mrp,
            shopId: product.shopId?._id || product.shopId || shopId
        });
    };

    const handleBuyNow = (e, product) => {
        e.stopPropagation();
        if (!AuthServ.isLogged()) {
            showAlert({
                title: "Authentication Required",
                description: "Please log in to proceed to checkout.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    { text: "Login", variant: "primary", onClick: () => navigate("/customer-login") }
                ]
            });
            return;
        }
        setCheckoutProduct(product);
        setCheckoutOpen(true);
    };

    const handleChatWithSupport = async () => {
        if (!AuthServ.isLogged()) {
            showAlert({
                title: "Continue Shopping",
                description: "Please login to chat with our support team.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    { text: "Login", variant: "primary", onClick: () => navigate("/customer-login") }
                ]
            });
            return;
        }

        try {
            const adminRes = await ChatServ.getSupportAdmin();
            const adminId = adminRes.data._id;

            const res = await ChatServ.startConversation({
                receiverId: adminId
            });
            setActiveConversation(res.data);
            setChatOpen(true);
        } catch (error) {
            console.error("Error starting chat:", error);
            message.error("Support is temporarily unavailable. Please try again later.");
        }
    };
    const shopData = shopInfo?.data || shopInfo || {};
    const business = shopData.business || {};
    const location = shopData.location || {};

    // Check shop closure state
    const isShopClosed = business.isOpen === false || business.isTemporarilyClosed === true;
    let closedMessage = "Closed until further notice.";
    if (business.reopenTimeUnknown) {
        closedMessage = "Reopening time not announced yet. Please check again later.";
    } else if (business.closedUntil) {
        // Need dayjs to format properly or use native Date
        const reopenDate = new Date(business.closedUntil);
        closedMessage = `Reopens at ${reopenDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} on ${reopenDate.toLocaleDateString()}`;
    }

    const handleResetFilters = () => {
        setMinPrice("");
        setMaxPrice("");
        setAvailability("all");
        setMinRating(0);
        setSelectedCategory("All");
        setSelectedSubCategoryId("all");
    };


    if (shopLoading) {
        return (
            <>
                <Navbar />
                <ShopDetailsSkeleton />
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="shop-details-wrapper">
                {/* BLOCKED STATE OVERLAY IF SHOP IS CLOSED */}
                {isShopClosed && (
                    <div className="ps-blocked-overlay" style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255,255,255,0.6)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', maxWidth: '400px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', border: '1px solid #f1f5f9' }}>
                            <div style={{ background: '#fef2f2', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                <IconMoodEmpty size={40} color="#dc2626" />
                            </div>
                            <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', marginBottom: '12px' }}>🏪 Shop Currently Closed</h2>
                            <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
                                This shop is temporarily unavailable.<br />
                                <strong style={{ color: '#0f172a' }}>{closedMessage}</strong>
                            </p>
                            <Button type="primary" size="large" onClick={() => navigate('/')} style={{ borderRadius: '8px', fontWeight: 600, height: '44px', padding: '0 30px' }}>
                                Browse Other Shops
                            </Button>
                        </div>
                    </div>
                )}

                <div className="shop-details-layout">

                    {/* LEFT: Shop Info & Filters Sidebar */}
                    <aside className="shop-sidebar">
                        {/* Shop Details Card */}
                        <div className="sidebar-card shop-info-card">
                            <div className="shop-header-mini">
                                <SafeImage src={shopData.imageUrl} alt={shopData.name} className="shop-avatar" placeholderText="Shop" />
                                <div>
                                    <GlobalTooltip title={renderSafeText(shopData.name)}>
                                        <h3 className="shop-name-sidebar">{renderSafeText(shopData.name)}</h3>
                                    </GlobalTooltip>
                                    <h3 className="shop-rating-sidebar">{renderSafeText(shopData.shopId)}</h3>
                                    {business.shopLicenseNumber && (
                                        <div className="shop-rating-sidebar" style={{ marginTop: '2px', color: '#64748b', fontSize: '12px' }}>
                                            License No: {business.shopLicenseNumber}
                                        </div>
                                    )}
                                    {shopData.averageRating > 0 && (
                                        <div className="shop-rating-sidebar">
                                            <IconStar size={14} fill="#ffc107" color="#ffc107" />
                                            <span>{shopData.averageRating} ({shopData.totalRatings || 0} reviews)</span>
                                        </div>
                                    )}
                                    {(shopData.description || shopData.shopDescription) && (
                                        <GlobalTooltip title={renderSafeText(shopData.description || shopData.shopDescription)} placement="topLeft">
                                            <p className="shop-description-tooltip">
                                                {renderSafeText(shopData.description || shopData.shopDescription)}
                                            </p>
                                        </GlobalTooltip>
                                    )}
                                </div>
                            </div>

                            <Divider className="pcu-margin-y-12" />

                            <div className="shop-meta-list">
                                <div className="meta-item">
                                    <IconMapPin size={18} className="meta-icon" style={{ flexShrink: 0, marginTop: '2px' }} />
                                    <GlobalTooltip title={renderSafeText(location.address || "Location unavailable")}>
                                        <span className="shop-meta-text" style={{ lineHeight: '1.4' }}>{renderSafeText(location.address || "Location unavailable")}</span>
                                    </GlobalTooltip>
                                </div>
                                <div className="meta-item" style={{ alignItems: 'center' }}>
                                    <IconClock size={18} className="meta-icon" style={{ flexShrink: 0 }} />
                                    <div className="delivery-pill" style={{ margin: 0 }}>
                                        <IconTruck size={14} />
                                        {formatETA(shopId) || (business.deliveryTime ? `${business.deliveryTime} delivery` : "Delivery N/A")}
                                    </div>
                                </div>
                                <div className="meta-item" style={{ alignItems: 'center' }}>
                                    <IconTruck size={18} className="meta-icon" style={{ flexShrink: 0 }} />
                                    <span>Min. Order ₹{business.minOrderValue || 0}</span>
                                </div>
                            </div>
                        </div>

                    </aside>

                    {/* RIGHT: Products Section */}
                    <div className="shop-main-content">
                        {/* Top Categories Bar */}
                        <div className="shop-categories-bar">
                            {[{ name: "All", id: "all" }, ...sidebarCategories].map(cat => {
                                const isSelected = selectedCategory === cat.name;
                                return (
                                    <div
                                        key={cat.id}
                                        className={`shop-category-tab ${isSelected ? 'active' : ''}`}
                                        onClick={() => handleCategoryClick(cat)}
                                    >
                                        {renderSafeText(cat.name)}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="products-grid-header">
                            <Title level={4} className="products-grid-header-title text-header">
                                {selectedCategory === "All" ? "All Products" : renderSafeText(selectedCategory)}
                                <span className="products-grid-count">
                                    ({products.length} items)
                                </span>
                            </Title>
                        </div>

                        {loading ? (
                            <div className="products-grid">
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <ProductCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="empty-products-container">
                                <div className="empty-products-icon-wrapper">
                                    <IconPackage size={60} color="#fff" stroke={1.5} />
                                </div>

                                <h3 className="empty-products-title">
                                    No Products Found
                                </h3>

                                <p className="empty-products-text">
                                    We couldn't find any products matching your search criteria. Try adjusting your filters or clearing them to see all available items.
                                </p>

                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={handleResetFilters}
                                    className="empty-products-btn"
                                >
                                    Clear All Filters
                                </Button>
                            </div>
                        ) : (
                            <div className="products-grid">
                                {products.map((product) => (
                                    <ProductCard
                                        key={product._id}
                                        product={product}
                                        hideShopInfo={true}
                                        formatETA={formatETA}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div >
            </div >

            {checkoutOpen && checkoutProduct && (
                <CheckoutModal
                    open={checkoutOpen}
                    onClose={() => setCheckoutOpen(false)}
                    items={[{ ...checkoutProduct, quantity: 1, selectedOptions: {} }]}
                />
            )
            }
            {
                chatOpen && (
                    <ChatWindow
                        open={chatOpen}
                        onClose={() => setChatOpen(false)}
                        conversation={activeConversation}
                        currentUser={currentUser}
                    />
                )
            }
        </>
    );
};

export default ShopDetails;
